import React, { useEffect, useRef, useState } from 'react';
import { Interweave } from 'interweave';
import { Editor } from 'tinymce';

import FileUploadField, { FileUploadHandler } from 'components/form-controls/FileUploadField';
import PaddedLoader from 'components/PaddedLoader';
import toast from 'helpers/toast';
import usePublicApi from 'public-api';
import { getErrors, isResponse } from 'public-api/utils';
import { useAsyncFunction } from 'stores/hooks';
import { Button, Card, Feed, Form, Popup } from 'ui';
import RichTextEditor from 'ui/rich-text-editor/RichTextEditor';

import MessageUser from './MessageUser';

type Props = {
  ticket: BackendPublic.TicketDetail;
  onSend?: () => Promise<void>;
};

function Communication({ ticket, onSend }: Props) {
  const [message, setMessage] = useState('');
  const [messagePreview, setMessagePreview] = useState<string | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [fileUploadInitialized, setFileUploadInitialized] = useState(false);
  const editorRef = useRef<Editor>();
  const fileUploadFieldRef = useRef<FileUploadHandler>();
  const api = usePublicApi();

  const sendMessage = async () => {
    setErrors({});
    setMessagePreview(message);

    try {
      await api.ClientTicketMessagesApi.create({
        id: ticket.id,
        request: {
          text: message,
          files,
        },
      });
      if (onSend) {
        await onSend();
      }
      setMessagePreview(null);
      editorRef.current?.execCommand('mceSetContent', false, '');
      setFiles([]);
    } catch (error) {
      if (isResponse(error) && error.response.status === 400) {
        const respErrors = await getErrors(error);
        setErrors(respErrors);
        toast.errorBadRequest();
      } else {
        toast.error();
      }
    }
  };

  const [handleSendMessage, isSending] = useAsyncFunction(sendMessage);

  useEffect(() => {
    if (fileUploadFieldRef.current) {
      setFileUploadInitialized(true);
    }
  }, [fileUploadFieldRef.current]);

  return (
    <Feed>
      <Feed.Event
        key={ticket.id}
        icon="chat-alt-2"
        feedIconRingColor="ring-gray-50"
        color="gray"
        renderChildren={(children) => (
          <div className="contents" role="presentation">
            <Card flat>
              <Card.Content>{children}</Card.Content>
            </Card>
          </div>
        )}
      >
        <Form onSubmit={() => handleSendMessage()}>
          <div className="space-y-4">
            {isEditorLoading && <PaddedLoader />}
            <div className={`relative flex-grow border ${isEditorLoading ? 'hidden' : ''}`}>
              {fileUploadInitialized && (
                <RichTextEditor
                  onChange={setMessage}
                  placeholder={gettext('Write a message...')}
                  onLoad={() => setIsEditorLoading(false)}
                  onInit={(evt, editor) => {
                    editorRef.current = editor;
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (!event.dataTransfer || !fileUploadFieldRef.current) return;
                    const filesArray = Array.from(event.dataTransfer.files);
                    filesArray.forEach((file) => {
                      fileUploadFieldRef.current?.handleFileUpload(file);
                    });
                  }}
                  onPaste={(event) => {
                    const filesArray = Array.from(event.clipboardData?.files);
                    if (filesArray.length > 0) {
                      event.preventDefault();
                      event.stopPropagation();
                      if (fileUploadFieldRef.current) {
                        filesArray.forEach((file) => {
                          fileUploadFieldRef.current?.handleFileUpload(file);
                        });
                      }
                    }
                  }}
                />
              )}
              {errors && errors.text && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    className="h-5 w-5 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>

            <FileUploadField
              ref={fileUploadFieldRef}
              value={files}
              label={gettext('Upload file/image')}
              onChange={(value) => setFiles(value)}
              setIsLoading={(value) => setIsFileUploading(value)}
              render={({ button, fileList }) => (
                <div className="space-y-4">
                  <div className={`${isSending && 'cursor-wait opacity-30'}`}>{fileList}</div>
                  <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
                    {button}
                    {isFileUploading ? (
                      <Popup
                        on="hover"
                        position="bottom right"
                        trigger={
                          <Button primary disabled>
                            {gettext('Send message')}
                          </Button>
                        }
                      >
                        <span className="whitespace-nowrap text-sm text-gray-600">
                          {gettext('Files are still uploading... Please wait.')}
                        </span>
                      </Popup>
                    ) : (
                      <Button primary submit loading={isSending}>
                        {gettext('Send message')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </Form>
      </Feed.Event>

      {messagePreview && (
        <Feed.Event
          icon="user"
          color="red"
          feedIconRingColor="ring-gray-50"
          renderChildren={(children) => (
            <div className="pointer-events-none animate-pulse cursor-wait opacity-30">
              <Card flat>
                <Card.Content>{children}</Card.Content>
              </Card>
            </div>
          )}
        >
          <div className="space-y-4">
            <div>
              <span className="font-medium">{ticket.customer_name}</span>
              <div className="text-sm text-gray-500">{gettext('Sending message...')}</div>
            </div>
            <p className="mb-0">
              <Interweave content={messagePreview} />
            </p>
          </div>
        </Feed.Event>
      )}

      {ticket.messages.map((message) => (
        <MessageUser key={message.id} ticket={ticket} message={message} />
      ))}
    </Feed>
  );
}

export default Communication;
